import React, { useState, useEffect } from 'react';
import { jobApplicationsAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import AdminLayout from '../components/AdminLayout';

const PipelinePage = () => {
  const [pipelineData, setPipelineData] = useState({});
  const [loading, setLoading] = useState(true);
  const [draggedItem, setDraggedItem] = useState(null);

  useEffect(() => {
    loadPipeline();
  }, []);

  const loadPipeline = async () => {
    try {
      setLoading(true);
      
      // Fetch from old API and group by status
      const response = await jobApplicationsAPI.getAll();
      
      // The API returns response.data which contains the applications array
      const applications = Array.isArray(response.data) ? response.data : (Array.isArray(response) ? response : []);
      
      // Group applications by status
      const grouped = {};
      const stages = ['new', 'screening', 'shortlisted', 'interview-scheduled', 'interviewed', 'offer', 'hired'];
      
      stages.forEach(stage => {
        grouped[stage] = {
          applications: [],
          count: 0,
          avgScore: 0
        };
      });
      
      applications.forEach(app => {
        const stage = app.status || 'new';
        if (grouped[stage]) {
          grouped[stage].applications.push(app);
        }
      });
      
      // Calculate counts and avg scores
      stages.forEach(stage => {
        const apps = grouped[stage].applications;
        grouped[stage].count = apps.length;
        
        if (apps.length > 0) {
          const scores = apps
            .map(app => {
              const score = app.overallScore || app.overall_score;
              return typeof score === 'string' ? parseFloat(score) : score;
            })
            .filter(score => !isNaN(score) && score > 0);
          
          if (scores.length > 0) {
            grouped[stage].avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
          }
        }
      });
      
      setPipelineData(grouped);
    } catch (error) {
      console.error('Failed to load pipeline:', error);
      alert('Failed to load pipeline: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, application, fromStage) => {
    setDraggedItem({ application, fromStage });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, toStage) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.fromStage === toStage) {
      setDraggedItem(null);
      return;
    }

    try {
      // Update status using old API
      await jobApplicationsAPI.updateStatus(draggedItem.application.id, toStage);
      loadPipeline();
    } catch (error) {
      console.error('Failed to update pipeline stage:', error);
      alert('Failed to move application');
    } finally {
      setDraggedItem(null);
    }
  };

  const getStageColor = (stage) => {
    const colors = {
      new: 'bg-blue-100 border-blue-300',
      screening: 'bg-yellow-100 border-yellow-300',
      shortlisted: 'bg-green-100 border-green-300',
      'interview-scheduled': 'bg-purple-100 border-purple-300',
      interviewed: 'bg-indigo-100 border-indigo-300',
      offer: 'bg-pink-100 border-pink-300',
      hired: 'bg-green-200 border-green-400'
    };
    return colors[stage] || 'bg-gray-100 border-gray-300';
  };

  const getScoreColor = (score) => {
    if (score >= 4.0) return 'text-green-600';
    if (score >= 3.0) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <AdminLayout>
        <LoadingSpinner />
      </AdminLayout>
    );
  }

  const stages = [
    { id: 'new', name: 'New' },
    { id: 'screening', name: 'Screening' },
    { id: 'shortlisted', name: 'Shortlisted' },
    { id: 'interview-scheduled', name: 'Interview Scheduled' },
    { id: 'interviewed', name: 'Interviewed' },
    { id: 'offer', name: 'Offer' },
    { id: 'hired', name: 'Hired' }
  ];

  return (
    <AdminLayout>
      <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Application Pipeline</h1>
        <p className="text-gray-600 mt-2">Drag and drop candidates between stages</p>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-6">
        {stages.map((stage) => {
          const stageData = pipelineData[stage.id] || { applications: [], count: 0, avgScore: 0 };
          const applications = Array.isArray(stageData.applications) ? stageData.applications : [];
          const count = stageData.count || applications.length || 0;
          const avgScore = stageData.avgScore || 0;
          
          return (
            <div
              key={stage.id}
              className="flex-shrink-0 w-80"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, stage.id)}
            >
              <div className={`rounded-lg border-2 ${getStageColor(stage.id)} p-4 h-full min-h-[600px]`}>
                {/* Stage Header */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-900">{stage.name}</h3>
                    <span className="px-2 py-1 bg-white rounded-full text-sm font-medium">
                      {count}
                    </span>
                  </div>
                  {count > 0 && avgScore > 0 && (
                    <p className="text-sm text-gray-600">
                      Avg Score: <span className="font-medium">{avgScore.toFixed(1)}</span>
                    </p>
                  )}
                </div>

                {/* Applications */}
                <div className="space-y-3">
                  {applications.map((application) => (
                    <div
                      key={application.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, application, stage.id)}
                      className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 cursor-move hover:shadow-md transition"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">
                            {application.firstName} {application.lastName}
                          </h4>
                          <p className="text-sm text-gray-500">{application.email}</p>
                        </div>
                        {(() => {
                          const score = application.overallScore || application.overall_score;
                          if (!score) return null;
                          const numScore = typeof score === 'string' ? parseFloat(score) : score;
                          return numScore > 0 && !isNaN(numScore) ? (
                            <span className={`text-lg font-bold ${getScoreColor(numScore)}`}>
                              {numScore.toFixed(1)}
                            </span>
                          ) : null;
                        })()}
                      </div>
                      
                      <div className="text-sm text-gray-600 mb-2">
                        {application.job?.title}
                      </div>

                      {application.interviewDate && (
                        <div className="text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded">
                          Interview: {new Date(application.interviewDate).toLocaleDateString()}
                        </div>
                      )}

                      <div className="mt-3 flex gap-2">
                        <a
                          href={`/admin/applications/${application.id}`}
                          className="text-xs text-blue-600 hover:text-blue-800"
                          onClick={(e) => e.stopPropagation()}
                        >
                          View Details
                        </a>
                      </div>
                    </div>
                  ))}

                  {applications.length === 0 && (
                    <div className="text-center py-8 text-gray-400">
                      <p className="text-sm">Drop candidates here</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-6 bg-white rounded-lg shadow p-4">
        <h3 className="font-semibold text-gray-900 mb-3">Score Legend</h3>
        <div className="flex gap-6">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-600"></div>
            <span className="text-sm text-gray-600">Excellent (4.0 - 5.0)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-600"></div>
            <span className="text-sm text-gray-600">Good (3.0 - 3.9)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-600"></div>
            <span className="text-sm text-gray-600">Needs Improvement (&lt; 3.0)</span>
          </div>
        </div>
      </div>
      </div>
    </AdminLayout>
  );
};

export default PipelinePage;
